/**
 * Auslastung: Minuten je Person und Monat gegen 160 h Kapazität.
 *
 * Je Monat gilt max(Ist, Plan) — `EmployeeRow.workloadMinutes` aus der Engine.
 * Ist = gebuchte Arbeit (Protokoll, nach Ausfuehrendem), Plan = Turnus und
 * Wochentage. Die Zelle sagt, welcher der beiden Werte gilt.
 */
import React, { useMemo } from 'react';
import { Euro, Gauge, Target } from 'lucide-react';
import type { CockpitModel, EmployeeRow } from '../../services/cockpitTypes';
import { Bar, Panel, Trend } from './CockpitBits';
import {
    CAP_HOURS, CAP_MINUTES, MO, MO_FULL, fmtCents, pctOf, workloadBorder, workloadFill,
} from './cockpitUi';

const TH = 'p-2 text-center text-[11px] font-semibold text-slate-400 uppercase';

/** Welcher Wert traegt den Monat — Ist (gebucht) oder Plan (Turnus)? */
const sourceOf = (e: EmployeeRow, i: number): 'Ist' | 'Plan' | null => {
    const ist = e.workloadIstMinutes[i] ?? 0;
    const plan = e.workloadPlanMinutes[i] ?? 0;
    if (ist <= 0 && plan <= 0) return null;
    return ist >= plan ? 'Ist' : 'Plan';
};

export interface WorkloadTabProps {
    model: CockpitModel;
    hideAmounts: boolean;
    todayMonth: number | null;
    onSelectMonth: (monthIndex: number) => void;
}

export const WorkloadTab: React.FC<WorkloadTabProps> = ({ model, hideAmounts, todayMonth, onSelectMonth }) => {
    // Innendienst ohne Termine und ohne Leistung bleibt draussen — sonst steht
    // die Haelfte des Rasters leer und der Team-Durchschnitt teilt durch zu viel.
    const rows = useMemo(
        () => model.employees
            .filter((e) => e.isFieldCoordinator || e.workloadMinutes.some((m) => m > 0) || e.performedCents !== 0)
            .sort((a, b) => b.workloadMinutes.reduce((s, v) => s + v, 0) - a.workloadMinutes.reduce((s, v) => s + v, 0)),
        [model.employees],
    );

    const lastMonth = model.periodMonths.length > 0 ? model.periodMonths[model.periodMonths.length - 1] : 11;

    const gaps = useMemo(() => {
        const from = todayMonth ?? 0;
        return rows.flatMap((e) => e.workloadMinutes
            .map((minutes, monthIndex) => ({
                name: e.name,
                monthIndex,
                free: Math.max(0, CAP_HOURS - Math.round(minutes / 60)),
                pct: Math.round((minutes / CAP_MINUTES) * 100),
            }))
            .filter((g) => g.monthIndex >= from && g.free >= 40))
            .sort((a, b) => b.free - a.free)
            .slice(0, 10);
    }, [rows, todayMonth]);

    return (
        <div data-glass-view="cockpit" className="space-y-5">
            <div data-glass="auf-fenster" className="flex flex-wrap items-center gap-4 text-[11px] font-semibold text-slate-500">
                <span>Kapazität: {CAP_HOURS} h/Monat und Person · je Monat der größere Wert aus Ist und Plan</span>
                <div className="flex flex-wrap items-center gap-2">
                    {[
                        { l: '<30 %', c: 'bg-emerald-100' }, { l: '30–60 %', c: 'bg-emerald-600' },
                        { l: '60–85 %', c: 'bg-amber-200' }, { l: '85–100 %', c: 'bg-orange-600' },
                        { l: '>100 %', c: 'bg-red-500' },
                    ].map((x) => (
                        <span key={x.l} className="flex items-center gap-1"><span className={`w-3 h-3 rounded ${x.c}`} />{x.l}</span>
                    ))}
                </div>
            </div>

            <Panel
                title={`Auslastung ${model.period.year}`}
                icon={<Gauge className="w-4 h-4 text-blue-500" />}
                bodyClassName="overflow-x-auto"
            >
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-slate-100">
                            <th className="p-2.5 pl-5 text-left text-[11px] font-semibold text-slate-400 uppercase w-40">Mitarbeiter</th>
                            {MO.map((m, i) => <th key={m} className={`${TH} w-14 ${i === todayMonth ? 'bg-blue-50' : ''}`}>{m}</th>)}
                            <th className={`${TH} bg-slate-50 w-16`}>Ø</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {rows.map((e) => {
                            const total = e.workloadMinutes.reduce((s, v) => s + v, 0);
                            const avg = Math.round((total / 12 / CAP_MINUTES) * 100);
                            return (
                                <tr key={e.id} className="hover:bg-slate-50/50">
                                    <td className="p-2.5 pl-5">
                                        <div className="text-xs font-semibold text-slate-700">{e.name}</div>
                                        <div className="text-[11px] text-slate-400">{e.projectCount} Projekte</div>
                                    </td>
                                    {e.workloadMinutes.map((minutes, i) => {
                                        const hours = Math.round(minutes / 60);
                                        const p = Math.round((minutes / CAP_MINUTES) * 100);
                                        const source = sourceOf(e, i);
                                        const istH = Math.round((e.workloadIstMinutes[i] ?? 0) / 60);
                                        const planH = Math.round((e.workloadPlanMinutes[i] ?? 0) / 60);
                                        return (
                                            <td key={i} className={`p-1 ${i === todayMonth ? 'bg-blue-50/50' : ''}`}>
                                                <button
                                                    onClick={() => onSelectMonth(i)}
                                                    className={`w-full h-12 rounded-lg flex flex-col items-center justify-center text-[11px] font-semibold border ${workloadFill(p)} ${workloadBorder(p)}`}
                                                    title={`${e.name} — ${MO_FULL[i]}: ${hours} h (${p} %) · Ist ${istH} h · Plan ${planH} h`}
                                                >
                                                    {hours > 0 ? (
                                                        <>
                                                            <span>{hours}h</span>
                                                            <span className="text-[11px] font-normal opacity-70">{p}%{source ? ` · ${source}` : ''}</span>
                                                        </>
                                                    ) : <span>—</span>}
                                                </button>
                                            </td>
                                        );
                                    })}
                                    <td className="p-1 bg-slate-50">
                                        <div className={`w-full h-12 rounded-lg flex items-center justify-center text-xs font-semibold border ${workloadFill(avg)} ${workloadBorder(avg)}`}>{avg}%</div>
                                    </td>
                                </tr>
                            );
                        })}
                        <tr className="bg-slate-50 border-t border-slate-200">
                            <td className="p-2.5 pl-5 text-xs font-semibold text-slate-700">Team Ø</td>
                            {Array.from({ length: 12 }, (_, i) => {
                                const sum = rows.reduce((s, e) => s + (e.workloadMinutes[i] ?? 0), 0);
                                const avg = rows.length ? Math.round(sum / rows.length / 60) : 0;
                                return <td key={i} className={`p-1.5 text-center text-[11px] font-semibold ${i === todayMonth ? 'bg-blue-50/50' : ''}`}>{avg}h</td>;
                            })}
                            <td className="p-1.5 bg-slate-100 text-center text-xs font-semibold">
                                {Math.round(rows.reduce((s, e) => s + e.workloadMinutes.reduce((a, b) => a + b, 0), 0) / Math.max(1, rows.length) / 12 / 60)}h
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div className="px-5 py-3 text-[11px] text-slate-400">
                    Vergangene Monate: gebuchte Arbeit (Tagessätze 8 h, Stunden 1:1). Kommende Monate: Planung aus Turnus und Wochentagen.
                    Im laufenden Monat gilt der größere Wert; „Ist“/„Plan“ in der Zelle nennt die Quelle.
                </div>
            </Panel>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                <Panel
                    title="Produktivität + Trend"
                    icon={<Euro className="w-4 h-4 text-emerald-500" />}
                    bodyClassName="overflow-x-auto"
                >
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-slate-100">
                                <th className="p-2.5 pl-5 text-left text-[11px] font-semibold text-slate-400 uppercase">Mitarbeiter</th>
                                <th className={TH}>{MO[lastMonth]}</th>
                                <th className={TH}>Vormonat</th>
                                <th className={TH}>Trend</th>
                                <th className={TH}>Σ Zeitraum</th>
                                <th className={TH}>Ø/Begehung</th>
                                <th className={TH}>Realisierung</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {[...rows].sort((a, b) => b.performedCents - a.performedCents).map((e) => {
                                const current = e.monthlyPerformed[lastMonth] ?? 0;
                                const realisation = pctOf(e.performedCents, e.forecastCents);
                                return (
                                    <tr key={e.id} className="hover:bg-slate-50/50">
                                        <td className="p-2.5 pl-5 text-xs font-semibold text-slate-700">{e.name}</td>
                                        <td className="p-2 text-center text-xs font-semibold text-emerald-700">{fmtCents(current, hideAmounts)}</td>
                                        <td className="p-2 text-center text-xs text-slate-500">{fmtCents(e.prevMonthPerformedCents, hideAmounts)}</td>
                                        <td className="p-2 text-center"><Trend current={current} previous={e.prevMonthPerformedCents} /></td>
                                        <td className="p-2 text-center text-xs font-semibold text-slate-700">{fmtCents(e.performedCents, hideAmounts)}</td>
                                        <td className="p-2 text-center text-xs text-indigo-600">
                                            {e.avgPerInspectionCents === null ? '—' : fmtCents(e.avgPerInspectionCents, hideAmounts)}
                                        </td>
                                        <td className="p-2">
                                            <div className="flex items-center justify-center gap-1">
                                                <div className="w-12">
                                                    <Bar value={realisation} max={100} height="h-1" color={realisation > 80 ? 'bg-emerald-500' : realisation > 50 ? 'bg-amber-400' : 'bg-red-400'} />
                                                </div>
                                                <span className="text-[11px] font-semibold text-slate-600">{realisation}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Panel>

                <Panel title="Kapazitätslücken" icon={<Target className="w-4 h-4 text-blue-500" />} hint="ab 40 freien Stunden">
                    <div className="space-y-2">
                        {gaps.length === 0 && (
                            <div className="py-6 text-center text-slate-400 text-[11px]">Keine Lücken ≥ 40 h in den betrachteten Monaten</div>
                        )}
                        {gaps.map((g, i) => (
                            <div key={`${g.name}-${g.monthIndex}-${i}`} className="flex items-center gap-3 p-2 rounded-lg border border-slate-100">
                                <div className="w-12 h-8 rounded bg-blue-50 text-blue-700 flex items-center justify-center text-[11px] font-semibold shrink-0">{g.free}h</div>
                                <div className="flex-1 min-w-0">
                                    <span className="text-xs font-semibold text-slate-700">{g.name}</span>
                                    <span className="text-[11px] text-slate-400"> · {MO_FULL[g.monthIndex]}</span>
                                </div>
                                <span className="text-[11px] text-slate-400 shrink-0">{g.pct} % belegt</span>
                            </div>
                        ))}
                    </div>
                </Panel>
            </div>
        </div>
    );
};
